import React, { useState } from "react";
import { Card, Dropdown, Badge, Spinner } from "react-bootstrap";
import AdminButton from "../../../components/admin/AdminButton";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import {
  faUsers,
  faTrash,
  faUserShield,
  faUserCheck,
  faTimes,
} from "@fortawesome/free-solid-svg-icons";
import { UserRole, UserStatus } from "../../../interfaces/adminUsers";
import { bulkUsers } from "../../../services/adminUsersService";
import ConfirmDialog from "./ConfirmDialog";

interface BulkBarProps {
  selected: string[];
  clearSelection: () => void;
  onDone: () => void;
}

export default function BulkBar({ selected, clearSelection, onDone }: BulkBarProps) {
  const [isLoading, setIsLoading] = useState(false);
  const [showDeleteConfirm, setShowDeleteConfirm] = useState(false);
  const [showRoleDropdown, setShowRoleDropdown] = useState(false);
  const [showStatusDropdown, setShowStatusDropdown] = useState(false);

  // Handle bulk role change
  const handleBulkRoleChange = async (role: UserRole) => {
    try {
      setIsLoading(true);
      await bulkUsers(selected, "setRole", { role });
      setShowRoleDropdown(false);
      clearSelection();
      onDone();
    } catch (error) {
      console.error("Bulk role change failed:", error);
      // You might want to show a toast notification here
    } finally {
      setIsLoading(false);
    }
  };

  // Handle bulk status change
  const handleBulkStatusChange = async (status: UserStatus) => {
    try {
      setIsLoading(true);
      await bulkUsers(selected, "setStatus", { status });
      setShowStatusDropdown(false);
      clearSelection();
      onDone();
    } catch (error) {
      console.error("Bulk status change failed:", error);
      // You might want to show a toast notification here
    } finally {
      setIsLoading(false);
    }
  };

  // Handle bulk delete
  const handleBulkDelete = async () => {
    try {
      setIsLoading(true);
      await bulkUsers(selected, "delete");
      setShowDeleteConfirm(false);
      clearSelection();
      onDone();
    } catch (error) {
      console.error("Bulk delete failed:", error);
      // You might want to show a toast notification here
    } finally {
      setIsLoading(false);
    }
  };

  return (
    <>
      <Card className="mb-3 border-primary">
        <Card.Body className="py-2">
          <div className="d-flex align-items-center justify-content-between">
            <div className="d-flex align-items-center gap-3">
              <div className="d-flex align-items-center gap-2">
                <FontAwesomeIcon icon={faUsers} className="text-primary" />
                <Badge bg="primary" className="fs-6">
                  {selected.length}
                </Badge>
                <span className="text-muted">
                  {selected.length === 1 ? "user selected" : "users selected"}
                </span>
              </div>

              {/* Bulk Actions */}
              <div className="d-flex gap-2">
                {/* Set Role Dropdown */}
                <Dropdown show={showRoleDropdown} onToggle={setShowRoleDropdown}>
                  <Dropdown.Toggle
                    variant="outline-primary"
                    size="sm"
                    disabled={isLoading}
                    className="d-flex align-items-center gap-1"
                  >
                    <FontAwesomeIcon icon={faUserShield} />
                    Set Role
                  </Dropdown.Toggle>
                  <Dropdown.Menu>
                    <Dropdown.Item
                      onClick={() => handleBulkRoleChange("admin")}
                      disabled={isLoading}
                    >
                      <div className="d-flex align-items-center gap-2">
                        <Badge bg="primary">Admin</Badge>
                        <span>Make Admin</span>
                      </div>
                    </Dropdown.Item>
                    <Dropdown.Item
                      onClick={() => handleBulkRoleChange("user")}
                      disabled={isLoading}
                    >
                      <div className="d-flex align-items-center gap-2">
                        <Badge bg="secondary">User</Badge>
                        <span>Make User</span>
                      </div>
                    </Dropdown.Item>
                  </Dropdown.Menu>
                </Dropdown>

                {/* Set Status Dropdown */}
                <Dropdown show={showStatusDropdown} onToggle={setShowStatusDropdown}>
                  <Dropdown.Toggle
                    variant="outline-warning"
                    size="sm"
                    disabled={isLoading}
                    className="d-flex align-items-center gap-1"
                  >
                    <FontAwesomeIcon icon={faUserCheck} />
                    Set Status
                  </Dropdown.Toggle>
                  <Dropdown.Menu>
                    <Dropdown.Item
                      onClick={() => handleBulkStatusChange("active")}
                      disabled={isLoading}
                    >
                      <div className="d-flex align-items-center gap-2">
                        <Badge bg="success">Active</Badge>
                        <span>Activate</span>
                      </div>
                    </Dropdown.Item>
                    <Dropdown.Item
                      onClick={() => handleBulkStatusChange("suspended")}
                      disabled={isLoading}
                    >
                      <div className="d-flex align-items-center gap-2">
                        <Badge bg="danger">Suspended</Badge>
                        <span>Suspend</span>
                      </div>
                    </Dropdown.Item>
                    <Dropdown.Item
                      onClick={() => handleBulkStatusChange("pending")}
                      disabled={isLoading}
                    >
                      <div className="d-flex align-items-center gap-2">
                        <Badge bg="warning">Pending</Badge>
                        <span>Set Pending</span>
                      </div>
                    </Dropdown.Item>
                  </Dropdown.Menu>
                </Dropdown>

                {/* Delete Button */}
                <AdminButton
                  intent="danger"
                  size="sm"
                  onClick={() => setShowDeleteConfirm(true)}
                  disabled={isLoading}
                  className="d-flex align-items-center gap-1"
                >
                  <FontAwesomeIcon icon={faTrash} />
                  Delete
                </AdminButton>
              </div>
            </div>

            {/* Clear Selection */}
            <AdminButton
              intent="secondary"
              size="sm"
              onClick={clearSelection}
              disabled={isLoading}
              className="d-flex align-items-center gap-1"
            >
              <FontAwesomeIcon icon={faTimes} />
              Clear
            </AdminButton>
          </div>

          {/* Loading Indicator */}
          {isLoading && (
            <div className="d-flex align-items-center gap-2 mt-2 text-muted">
              <Spinner size="sm" />
              <small>Processing bulk action...</small>
            </div>
          )}
        </Card.Body>
      </Card>

      {/* Delete Confirmation Dialog */}
      <ConfirmDialog
        show={showDeleteConfirm}
        title="Confirm Bulk Delete"
        message={`Are you sure you want to delete ${selected.length} ${
          selected.length === 1 ? "user" : "users"
        }? This action cannot be undone.`}
        confirmText="Delete"
        confirmVariant="danger"
        onConfirm={handleBulkDelete}
        onCancel={() => setShowDeleteConfirm(false)}
        isLoading={isLoading}
      />
    </>
  );
}
